import { Preferences } from '@capacitor/preferences'
import { Capacitor } from '@capacitor/core'

const PUSH_TOKEN_KEY = 'push_device_token'

export function usePushNotifications() {
  const deviceToken = ref<string | null>(null)
  const permissionGranted = ref(false)

  async function register() {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications')

      const perm = await PushNotifications.requestPermissions()
      if (perm.receive !== 'granted') {
        permissionGranted.value = false
        return
      }
      permissionGranted.value = true

      await PushNotifications.register()

      PushNotifications.addListener('registration', async (token) => {
        deviceToken.value = token.value
        await Preferences.set({ key: PUSH_TOKEN_KEY, value: token.value })
        const auth = useAuthStore()
        if (auth.accessToken) {
          try {
            const api = useApi()
            const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android'
            await api.post('/auth/push/register/', { token: token.value, platform })
          } catch {}
        }
      })

      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        const data = notification.data as Record<string, string> | undefined
        // Chat: incrementa unread no contador
        if (data?.type === 'chat_message') {
          const { addUnread } = useChatUnread()
          const senderId = data.sender_id ? parseInt(data.sender_id) : undefined
          const isDirect = data.is_direct === 'true'
          addUnread(senderId, isDirect)
        }
        // Toast apenas se não for chat (chat já tem badge)
        if (data?.type !== 'chat_message') {
          const toast = useToast()
          toast.add({
            title: notification.title || 'Nova notificação',
            description: notification.body,
            color: 'primary',
          })
        }
      })

      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const router = useRouter()
        const data = action.notification.data as Record<string, string> | undefined
        if (data?.type === 'chat_message') {
          router.push(data.is_direct === 'true' && data.sender_id
            ? `/app/chat?dm=${data.sender_id}`
            : '/app/chat')
        } else if (data?.screen) {
          router.push(data.screen)
        }
      })
    } catch {}
  }

  async function unregister() {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications')
      await PushNotifications.unregister()
      deviceToken.value = null
      await Preferences.remove({ key: PUSH_TOKEN_KEY })
      const auth = useAuthStore()
      if (auth.accessToken) {
        try {
          const api = useApi()
          await api.post('/auth/push/unregister/')
        } catch {}
      }
    } catch {}
  }

  async function restoreToken() {
    const { value } = await Preferences.get({ key: PUSH_TOKEN_KEY })
    if (value) deviceToken.value = value
  }

  return {
    deviceToken,
    permissionGranted,
    register,
    unregister,
    restoreToken,
  }
}
